import { createRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { rootRoute } from '../root.js';
import {
  isPlatformAuthenticated,
  PlatformApiError,
} from '../../lib/platform-auth.js';
import {
  platform2faSetup,
  platform2faVerify,
  platformLogin,
  type Platform2faSetupResult,
} from '../../api/platform.js';

/**
 * /platform/login — operator login with MANDATORY TOTP 2FA.
 *
 * Three phases:
 *   1. credentials  — email + password → POST /login (stores partial token)
 *   2a. setup       — first-time enrollment: POST /2fa/setup → QR + key
 *   2b. verify      — 6-digit code → POST /2fa/verify (stores full token)
 *
 * Mirrors the tenant login.tsx (react-hook-form + zodResolver, ApiError →
 * friendly messages), with the extra 2FA steps and the platform visual skin.
 */
export const platformLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/login',
  beforeLoad: () => {
    if (isPlatformAuthenticated()) {
      throw redirect({ to: '/platform/tenants' });
    }
  },
  component: PlatformLoginPage,
});

type Phase = 'credentials' | 'setup' | 'verify';

const CredentialsForm = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required').max(128),
});
type CredentialsForm = z.infer<typeof CredentialsForm>;

const VerifyForm = z.object({
  code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code'),
});
type VerifyForm = z.infer<typeof VerifyForm>;

function PlatformLoginPage(): JSX.Element {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>('credentials');
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Platform — Sign in';
  }, []);

  return (
    <main className="min-h-screen flex items-center justify-center bg-indigo-deep text-gray-900">
      <div className="w-full max-w-sm space-y-6 p-8 bg-white rounded-lg shadow-xl">
        <header className="text-center space-y-1">
          <h1 className="text-xl font-semibold text-indigo-deep">Platform Operator Console</h1>
          <p className="text-sm text-gray-500">
            {phase === 'credentials'
              ? 'Sign in to continue'
              : phase === 'setup'
                ? 'Set up two-factor authentication'
                : 'Enter your authentication code'}
          </p>
        </header>

        {serverError ? (
          <p role="alert" className="text-sm text-red-600 text-center">
            {serverError}
          </p>
        ) : null}

        {phase === 'credentials' ? (
          <CredentialsStep
            onError={setServerError}
            onSuccess={(requiresSetup) => {
              setServerError(null);
              setPhase(requiresSetup ? 'setup' : 'verify');
            }}
          />
        ) : null}

        {phase === 'setup' ? (
          <SetupStep onError={setServerError} onEnrolled={() => { setServerError(null); setPhase('verify'); }} />
        ) : null}

        {phase === 'verify' ? (
          <VerifyStep
            onError={setServerError}
            onVerified={() => void navigate({ to: '/platform/tenants' })}
          />
        ) : null}
      </div>
    </main>
  );
}

function CredentialsStep({
  onSuccess,
  onError,
}: {
  onSuccess: (requiresSetup: boolean) => void;
  onError: (msg: string | null) => void;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CredentialsForm>({
    resolver: zodResolver(CredentialsForm),
    defaultValues: { email: '', password: '' },
  });

  async function onSubmit(values: CredentialsForm): Promise<void> {
    onError(null);
    try {
      const res = await platformLogin(values);
      onSuccess(res.requiresSetup);
    } catch (err) {
      if (err instanceof PlatformApiError && err.status === 401) {
        onError('Invalid email or password.');
      } else if (err instanceof PlatformApiError && err.status === 429) {
        onError('Too many attempts. Wait a minute and try again.');
      } else {
        onError('Sign in failed. Try again.');
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <Field id="email" label="Email" type="email" autoComplete="email" error={errors.email?.message} inputProps={register('email')} />
      <Field id="password" label="Password" type="password" autoComplete="current-password" error={errors.password?.message} inputProps={register('password')} />
      <SubmitButton pending={isSubmitting} idle="Sign in" busy="Signing in..." />
    </form>
  );
}

function SetupStep({
  onEnrolled,
  onError,
}: {
  onEnrolled: () => void;
  onError: (msg: string | null) => void;
}): JSX.Element {
  const [setup, setSetup] = useState<Platform2faSetupResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await platform2faSetup();
        if (!cancelled) setSetup(res);
      } catch {
        if (!cancelled) onError('Could not start 2FA setup. Try signing in again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onError]);

  if (loading) {
    return <p className="text-sm text-gray-500 text-center">Preparing setup…</p>;
  }
  if (!setup) {
    return <p className="text-sm text-gray-500 text-center">Setup unavailable.</p>;
  }

  return (
    <div className="space-y-4 text-center">
      <p className="text-sm text-gray-600">
        Scan this QR code with your authenticator app, then continue.
      </p>
      <img src={setup.qrCodeDataUri} alt="TOTP enrollment QR code" className="mx-auto h-44 w-44" />
      <div className="text-xs text-gray-500">
        Or enter this key manually:
        <code className="block mt-1 break-all rounded bg-gray-100 px-2 py-1 font-mono text-gray-700">
          {setup.manualEntryKey}
        </code>
      </div>
      <button
        type="button"
        onClick={onEnrolled}
        className="w-full rounded bg-indigo-deep text-white font-medium py-2 hover:bg-indigo-deep/90"
      >
        I&apos;ve enrolled — verify
      </button>
    </div>
  );
}

function VerifyStep({
  onVerified,
  onError,
}: {
  onVerified: () => void;
  onError: (msg: string | null) => void;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<VerifyForm>({
    resolver: zodResolver(VerifyForm),
    defaultValues: { code: '' },
  });

  async function onSubmit(values: VerifyForm): Promise<void> {
    onError(null);
    try {
      await platform2faVerify(values);
      onVerified();
    } catch (err) {
      if (err instanceof PlatformApiError && err.status === 401) {
        onError('Invalid code, try again.');
      } else if (err instanceof PlatformApiError && err.status === 429) {
        onError('Too many attempts. Wait a minute and try again.');
      } else {
        onError('Verification failed. Try again.');
      }
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
      <Field
        id="code"
        label="6-digit code"
        type="text"
        autoComplete="one-time-code"
        inputMode="numeric"
        placeholder="000000"
        error={errors.code?.message}
        inputProps={register('code')}
      />
      <SubmitButton pending={isSubmitting} idle="Verify" busy="Verifying..." />
    </form>
  );
}

// ---------------------------------------------------------------------------
// Small shared form bits
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  inputMode?: 'numeric';
  error?: string;
  inputProps: ReturnType<ReturnType<typeof useForm<CredentialsForm & VerifyForm>>['register']>;
}

function Field({ id, label, type = 'text', placeholder, autoComplete, inputMode, error, inputProps }: FieldProps): JSX.Element {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs uppercase tracking-wide text-gray-500">
        {label}
      </label>
      <input
        id={id}
        type={type}
        placeholder={placeholder}
        autoComplete={autoComplete}
        inputMode={inputMode}
        aria-invalid={error ? 'true' : 'false'}
        className="w-full rounded border border-gray-300 bg-white px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-indigo-deep focus:outline-none"
        {...inputProps}
      />
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function SubmitButton({ pending, idle, busy }: { pending: boolean; idle: string; busy: string }): JSX.Element {
  return (
    <button
      type="submit"
      disabled={pending}
      className="w-full rounded bg-indigo-deep text-white font-medium py-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-indigo-deep/90"
    >
      {pending ? busy : idle}
    </button>
  );
}
